import axios from './index';

import type { ApiResponse } from './dramas';

/** 审核状态：0 未审核、1 已审核 */
export type MiningWeeklyRewardAuditStatus = 0 | 1;

/** 奖励类型：0 派遣收益、1 邀请收益 */
export type MiningWeeklyRewardType = 0 | 1;

export interface MiningWeeklyRewardListParams {
    page?: number | string;
    size?: number | string;
    /** 审核状态，0 未审核、1 已审核 */
    audit_status?: string;
    /** 奖励类型，0 派遣收益、1 邀请收益 */
    reward_type?: string;
    /** 用户 ID */
    user_id?: string;
    /** 周次（详情页按周期拉取时使用，后端若忽略则前端再筛） */
    week_n?: number | string;
}

export interface MiningWeeklyRewardItem {
    id: string;
    user_id: string;
    week_n: number;
    start_time: string;
    end_time: string;
    nominal_output: string;
    actual_output: string;
    audit_status: MiningWeeklyRewardAuditStatus;
    audit_at: string | null;
    audit_by: string | null;
    audit_remark: string | null;
    scaled: string;
    reward_type: MiningWeeklyRewardType;
    from_user_id: string | null;
    created_at: string;
}

/**
 * 按周聚合列表项（对齐 GET /v1/mining-weekly-rewards/group-by-week）
 * 原始：{ week_n, start_time, end_time, audit_status, audit_at,
 *        _count: { _all }, _sum: { nominal_output, actual_output, scaled } }
 */
export interface MiningWeeklyRewardWeekGroup {
    week_n: number;
    /** 该周奖励记录条数（来自 _count._all） */
    record_count?: number;
    actual_output?: string;
    nominal_output?: string;
    scaled?: string;
    start_time?: string;
    end_time?: string;
    user_count?: number;
    audit_status?: MiningWeeklyRewardAuditStatus;
    audit_at?: string | null;
    audit_by?: string | null;
    auto_release_at?: string | null;
}

export type { ApiResponse };

export interface MiningWeeklyRewardApproveItem {
    id: number;
    /** 放行时的实际产出；未改则传原始 actual_output */
    reward: number;
    memo?: string;
}

export interface MiningWeeklyRewardApprovePayload {
    rewards: MiningWeeklyRewardApproveItem[];
}

export interface MiningWeeklyRewardApprovePeriodPayload {
    weekN: number;
    auditBy?: string;
    auditRemark?: string;
}

export const normalizeWeekGroup = (raw: Record<string, any>): MiningWeeklyRewardWeekGroup => {
    const weekN = Number(raw.week_n ?? raw.weekN ?? raw.week ?? 0);
    const countAll = raw._count?._all ?? raw.record_count ?? raw.user_count ?? raw.userCount ?? raw.users;
    const sum = raw._sum ?? {};
    const actualOutput = sum.actual_output ?? raw.actual_output ?? raw.actualOutput ?? raw.amount;
    const nominalOutput = sum.nominal_output ?? raw.nominal_output ?? raw.nominalOutput;
    const scaled = sum.scaled ?? raw.scaled;
    const auditStatus = raw.audit_status ?? raw.auditStatus;
    const auditAt = raw.audit_at ?? raw.auditAt ?? null;

    return {
        week_n: Number.isFinite(weekN) ? weekN : 0,
        record_count:
            countAll === undefined || countAll === null || countAll === ''
                ? undefined
                : Number(countAll),
        user_count:
            countAll === undefined || countAll === null || countAll === ''
                ? undefined
                : Number(countAll),
        actual_output:
            actualOutput === undefined || actualOutput === null ? undefined : String(actualOutput),
        nominal_output:
            nominalOutput === undefined || nominalOutput === null
                ? undefined
                : String(nominalOutput),
        scaled: scaled === undefined || scaled === null ? undefined : String(scaled),
        start_time: raw.start_time ?? raw.startTime,
        end_time: raw.end_time ?? raw.endTime,
        audit_status:
            auditStatus === undefined || auditStatus === null || auditStatus === ''
                ? undefined
                : (Number(auditStatus) as MiningWeeklyRewardAuditStatus),
        audit_at: auditAt,
        audit_by: raw.audit_by ?? raw.auditBy ?? null,
        auto_release_at: raw.auto_release_at ?? raw.autoReleaseAt ?? null,
    };
};

export const miningWeeklyRewardList = (params?: MiningWeeklyRewardListParams) =>
    axios<ApiResponse<MiningWeeklyRewardItem[]>>('/v1/mining-weekly-rewards', {
        method: 'get',
        params,
    });

export const miningWeeklyRewardGroupByWeek = (params?: MiningWeeklyRewardListParams) =>
    axios<ApiResponse<Record<string, any>[]>>('/v1/mining-weekly-rewards/group-by-week', {
        method: 'get',
        params,
    });

export const miningWeeklyRewardApprove = (data: MiningWeeklyRewardApprovePayload) =>
    axios<ApiResponse>('/v1/mining-weekly-rewards/approve', {
        method: 'post',
        data,
    });

export const miningWeeklyRewardApprovePeriod = (data: MiningWeeklyRewardApprovePeriodPayload) =>
    axios<ApiResponse>('/v1/mining-weekly-rewards/approve-period', {
        method: 'post',
        data,
    });

/** 按周期导出 */
export const miningWeeklyRewardExport = (params?: { week_n?: number | string }) =>
    axios<ApiResponse>('/v1/mining-weekly-rewards/export', {
        method: 'get',
        params,
    });
